<template>
  <div>
    <section class="content-group -bg-01 -center -no-spacer -vs-medium -column">
      <div class="group -flex -between -narrow -vs-small -vs-bottom -vs-collapse-bottom">
        <h1 id="spa-title" class="heading -primary">Daily Energy Needs</h1>
        <div class="spa-heading-action">
          <router-link to="/my/settings/energy">Back</router-link>
        </div>
      </div>
      <div class="group -narrow -vs-small -vs-collapse-top">
        <h2 class="heading -tertiary">
          I want to set my daily energy needs based on one of the following options:
        </h2>
      </div>

      <!-- General Range -->
      <router-link
        to="/my/settings/energy/calorie/range"
        class="preference-card group -flex -between -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double -range"
      >
        <div class="selection-text">
          <h2 class="heading -secondary">General Range</h2>
          <p>
            I do not want to see the calorie and macronutrient information for meal plans, so I will
            use a general range and adjust what I need based on how I feel.
          </p>
        </div>
        <div class="selection-icon">
          <i class="material-symbols-outlined -rotate-neg-90">expand_circle_down</i>
        </div>
      </router-link>
      <!-- End General Range -->

      <!-- Options Message -->
      <div
        v-if="dietaryMethod === 'mindful'"
        class="options-message group -narrow -vs-small -vs-center -hs-medium -bg-01 -stack-space-double"
      >
        <div class="selection-text">
          <h2 class="heading -tertiary">
            Based on your Mindful Eating selection, calorie estimate options are disabled. You can
            still start with a calorie estimate if you prefer, but calories and macronutrients will
            not show in your meal plans.
          </h2>
        </div>
        <div class="spa-action">
          <button class="btn -main" @click="toggleShowAll">
            {{ showAll ? 'Hide' : 'Enable' }} Calorie Options
          </button>
        </div>
      </div>
      <!-- End Options Message -->

      <!-- Calorie Goal -->
      <router-link :to="showAll ? '/my/settings/energy/calorie/goal' : ''" custom>
        <template #default="{ href, navigate, isActive }">
          <component
            :is="showAll ? 'a' : 'div'"
            class="preference-card group -flex -between -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double"
            :href="href"
            :class="{
              '-goal': showAll,
              '-deselected': !showAll,
              '-no-pointer': !showAll,
              active: isActive,
            }"
            @click="navigate"
          >
            <div class="selection-text">
              <h2 class="heading -secondary">Calorie Goal</h2>
              <p>
                I want to see the calorie and macronutrient information for meal plans, and I have
                an approximate number of calories I want to target.
              </p>
            </div>
            <div class="selection-icon">
              <i class="material-symbols-outlined -rotate-neg-90">expand_circle_down</i>
            </div>
          </component>
        </template>
      </router-link>
      <!-- End Calorie Goal -->

      <!-- Calorie Calculator -->
      <router-link :to="showAll ? '/my/settings/energy/calorie/calculator' : ''" custom>
        <template #default="{ href, navigate, isActive }">
          <component
            :is="showAll ? 'a' : 'div'"
            :href="href"
            class="preference-card group -flex -between -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double"
            :class="{
              '-calculator': showAll,
              '-deselected': !showAll,
              '-no-pointer': !showAll,
              active: isActive,
            }"
            @click="navigate"
          >
            <div class="selection-text">
              <h2 class="heading -secondary">Calorie Calculator</h2>
              <p>
                I want to see the calorie and macronutrient information for meal plans, and I want
                to use a calculator to give me an estimate of the calories I need.
              </p>
            </div>
            <div class="selection-icon">
              <i class="material-symbols-outlined -rotate-neg-90">expand_circle_down</i>
            </div>
          </component>
        </template>
      </router-link>
      <!-- End Calorie Calculator -->
    </section>

    <more-info-section title="Energy, Calories and Estimates">
      <p>
        Your Daily Energy Needs represent the amount of food you need to eat each day related to the
        amount of energy you use. Typically this energy is measured in calories. However, even the
        most sophisticated tests and diligent food tracking will only provide you with an estimate
        of the actual calories you eat versus what your body uses. Regardless of the energy estimate
        method you choose, just remember that it is still just an estimate, and you will need to pay
        attention to your body and how you feel to fine tune your diet to meet your personal needs.
      </p>
    </more-info-section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import MoreInfoSection from './more-info-section.vue'

/**
 * Step 3 - Energy (cal) Method Selection
 *
 * Just navigation between the three steps, does not save on click
 */
export default defineComponent({
  name: 'CalorieStep',
  components: {
    MoreInfoSection,
  },
  props: {
    dietaryMethod: {
      type: String,
      default: '',
    },

    // loading state from parent
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAll: this.dietaryMethod !== 'mindful',
    }
  },

  computed: {},

  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll
    },
  },
})
</script>
